import { z } from "zod";

/**
 * Haiku is the sole brain in v1. One call per heartbeat / position event.
 * Returns a safety status PLUS an optional action payload, so the worker
 * can both gate unsafe state AND emit the next trade command without a
 * second round trip.
 *
 * Status meanings
 *   continue — state healthy, nothing to do, sleep until next heartbeat.
 *   hold     — state is ambiguous, wait until `holdUntilMs`. HedgePair DO
 *              skips heartbeat-triggered workflow runs until that deadline.
 *   alert    — hard violation imminent. Broadcast + optionally auto-close.
 *   execute  — perform the attached `action` now.
 */
/**
 * Flat action shape — matches what Anthropic structured output returns
 * (it doesn't support oneOf/discriminatedUnion at the schema level).
 * Every per-kind field is nullable; `kind` selects which fields carry
 * meaning. Callers should switch on `action.kind` and trust the fields
 * the persona maps to that kind; other fields are expected null.
 */
export const HaikuActionZ = z.object({
  kind: z.enum([
    "open_trade",
    "close_pair",
    "news_protect",
    "news_restore",
    "done_for_day",
    "sanity_check_sl",
  ]),
  // open_trade + news_restore
  symbol: z.string().min(1).max(16).nullable().optional(),
  lots: z.number().nullable().optional(),
  buyOn: z.enum(["long", "short"]).nullable().optional(),
  slDistancePrice: z.number().nullable().optional(),
  tpDistancePrice: z.number().nullable().optional(),
  // close_pair + done_for_day
  reason: z.string().max(200).nullable().optional(),
  // news_protect
  maxLossDollars: z.number().nullable().optional(),
  maxLossPctOfDailyRemaining: z.number().nullable().optional(),
  // sanity_check_sl (internal)
  side: z.enum(["long", "short"]).nullable().optional(),
  entry: z.number().nullable().optional(),
  proposedSl: z.number().nullable().optional(),
  expectedLossDollars: z.number().nullable().optional(),
  maxAllowedDollars: z.number().nullable().optional(),
  approved: z.boolean().nullable().optional(),
});
export type HaikuAction = z.infer<typeof HaikuActionZ>;

/**
 * Raw shape Anthropic structured output returns. Haiku serializes the
 * `action` object as a JSON string (workaround for the validator not
 * supporting `oneOf` / nullable-union types). We parse + re-validate
 * with `HaikuActionZ` on the worker side.
 */
export const HaikuDecisionRawZ = z.object({
  status: z.enum(["continue", "hold", "alert", "execute"]),
  holdUntilMs: z.number().int(),
  actionJson: z.string(),
  rulesChecked: z.array(z.string()),
  reason: z.string().max(500),
  confidence: z.number(),
});
export type HaikuDecisionRaw = z.infer<typeof HaikuDecisionRawZ>;

/** Parsed shape used everywhere downstream. */
export const HaikuDecisionZ = z.object({
  status: z.enum(["continue", "hold", "alert", "execute"]),
  /** Only meaningful when status = "hold". Epoch ms. 0 = not applicable. */
  holdUntilMs: z.number().int().nullable(),
  /** Present when status = "execute". Null for continue/hold/alert. */
  action: HaikuActionZ.nullable(),
  /** Canonical rule identifiers the model verified. Audit evidence. */
  rulesChecked: z.array(z.string()),
  reason: z.string().max(500),
  confidence: z.number().min(0).max(1),
});
export type HaikuDecision = z.infer<typeof HaikuDecisionZ>;

// ── Legacy types kept for back-compat while call sites migrate ──
//
// HaikuGateDecisionZ is the pre-v1 shape (continue|escalate|alert only).
// sonnet-decision.ts and existing tests still reference it. Keep the
// export until the workflow is fully converted, then delete.

/** @deprecated Use HaikuDecisionZ. Kept while Sonnet code still compiles. */
export const HaikuGateDecisionZ = z.object({
  status: z.enum(["continue", "escalate", "alert"]),
  reason: z.string().max(500),
  nextCheckInSeconds: z.number().int().positive().max(3600),
});
export type HaikuGateDecision = z.infer<typeof HaikuGateDecisionZ>;

/**
 * Full Sonnet compliance decision. Legal-grade audit record.
 * Every field becomes part of the permanent D1 audit_log row.
 * Not invoked in v1 — kept for compliance-dispute re-runs.
 */
export const SonnetDecisionZ = z.object({
  action: z.enum(["execute", "hold", "block"]),
  reason: z.string().max(1000),
  rulesChecked: z.array(z.string()).min(1),
  complianceStatus: z.enum(["compliant", "warning", "violation"]),
  violationDetail: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  nextCheckInSeconds: z.number().int().positive().max(3600),
});
export type SonnetDecision = z.infer<typeof SonnetDecisionZ>;

/** What the hedge system wants to do, sent to Sonnet for approval. */
export const ProposedActionZ = z.object({
  kind: z.enum(["open", "close", "modify", "extract", "swap", "pair-add", "pair-remove"]),
  side: z.enum(["long", "short"]).nullable(),
  symbol: z.string().nullable(),
  lots: z.number().nullable(),
  reason: z.string(),
});
export type ProposedAction = z.infer<typeof ProposedActionZ>;
